import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type User } from "../lib/api";
import { LoadingButton, LoadingLinkButton, InlineLoading } from "../components/Spinner";

export default function InvitePage({ user }: { user: User | null }) {
  const { token = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api.invite.lookup(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: (id: string) => api.acceptInvite(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      qc.invalidateQueries({ queryKey: ["my-invites"] });
      nav("/");
    },
  });

  const inviteEmail = q.data?.email ?? "";
  const wrongAccount =
    user && inviteEmail && user.email.toLowerCase() !== inviteEmail.toLowerCase();

  return (
    <div className="grid h-full place-items-center bg-bg p-6">
      <div className="card max-w-md w-full p-8 space-y-5 text-center">
        {q.isLoading && <div className="grid place-items-center"><InlineLoading label="Loading invite…" /></div>}
        {q.isError && (
          <p className="text-danger">This invite link is invalid or has expired.</p>
        )}
        {q.data && (
          <>
            <h1 className="text-xl font-semibold">You're invited!</h1>
            <p className="text-sm muted">
              <b className="text-text">{q.data.inviter_name ?? q.data.inviter_email}</b>
              {" invited "}
              <b className="text-text">{q.data.email}</b>
              {" to share "}
              <b className="text-text">{q.data.workspace_name}</b> on rest.
            </p>

            {q.data.redeemed_at && (
              <p className="text-danger">This invite has already been accepted.</p>
            )}

            {!q.data.redeemed_at && user && !wrongAccount && (
              <LoadingButton
                className="btn-primary w-full justify-center"
                pending={accept.isPending}
                pendingLabel="Joining…"
                onClick={() => accept.mutate(q.data.id)}
              >
                Accept invite
              </LoadingButton>
            )}

            {!q.data.redeemed_at && user && wrongAccount && (
              <div className="space-y-3 text-sm">
                <p className="text-warn">
                  You're signed in as <b>{user.email}</b>, but this invite is for{" "}
                  <b>{q.data.email}</b>. Sign out and use that email to accept.
                </p>
                <Link to="/" className="text-accent-bright hover:underline">
                  Go to your workspace
                </Link>
              </div>
            )}

            {!q.data.redeemed_at && !user && (
              <div className="space-y-3">
                <p className="text-sm muted">Sign in or create an account to accept.</p>
                <LoadingLinkButton
                  href={`/api/auth/google/start?next=${encodeURIComponent("/")}&invite=${encodeURIComponent(token)}`}
                  className="btn-neutral w-full justify-center"
                  pendingLabel="Connecting…"
                  startIcon={<GoogleIcon />}
                >
                  Continue with Google
                </LoadingLinkButton>
                <div className="flex items-center gap-3 text-xs muted-strong">
                  <div className="flex-1 h-px bg-border" />
                  or
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="flex flex-col gap-2">
                  <Link
                    to={`/login?invite=${encodeURIComponent(token)}&next=${encodeURIComponent("/")}`}
                    className="btn w-full justify-center"
                  >
                    Sign in with email
                  </Link>
                  <Link
                    to={`/login?invite=${encodeURIComponent(token)}&next=${encodeURIComponent("/")}&mode=signup`}
                    className="btn w-full justify-center"
                  >
                    Create an account
                  </Link>
                </div>
              </div>
            )}

            {accept.isError && (
              <p className="text-xs text-danger">
                Couldn't accept this invite. Try refreshing.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6 28.9 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6.9 28.9 4.9 24 4.9 16.1 4.9 9.3 9 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.3-5.2l-6.1-5.2C29.2 35 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8.2l-6.6 5.1C9.1 39.8 16 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.1 5.2C40.7 35.6 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
